import React, { useState } from 'react';
import { Form, Formik } from 'formik';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_STATUS_LOST,
  INVENTORY_ITEM_TYPES_OPTIONS,
} from 'common/inventoryConstants';

import {
  SR_API_ACTION_INVENTORY_ITEM_SCAN,
  SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
} from 'api/actionTypes';

import { createFormValidationFn, hideFieldIf } from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE_BORDER,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrText from 'client/components/SrText';
import postWithState from 'client/actions/postWithState';
import useDispatch from 'client/hooks/useDispatch';

import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const SCANNER_STATE_OPEN = 'open';
const SCANNER_STATE_CLOSED = 'closed';
const SCANNER_STATE_MANUAL = 'manual';

export const FORM_TYPE_REGISTER = 'register';
export const FORM_TYPE_SCAN = 'scan';

function getFormFields({ formType, scannerState }) {
  return [
    hideFieldIf(formType === FORM_TYPE_SCAN, {
      id: 'type',
      title: 'Item Type',
      type: 'select',
      properties: {
        choices: INVENTORY_ITEM_TYPES_OPTIONS,
      },
      validations: ['required'],
    }),
    hideFieldIf(formType === FORM_TYPE_SCAN, {
      id: 'status',
      title: 'Item Status',
      type: 'select',
      properties: {
        choices: INVENTORY_ITEM_STATUSES_OPTIONS.filter(
          ({ value }) => value !== INVENTORY_ITEM_STATUS_LOST,
        ),
      },
      validations: ['required'],
    }),
    hideFieldIf(scannerState !== SCANNER_STATE_MANUAL, {
      id: 'serialNumber',
      title: 'Serial Number',
      type: 'short_text',
      placeholder: 'Enter serial number',
      validations: ['required'],
    }),
  ];
}

const SrInventoryItemScanForm = ({
  formType,
  itemType,
  getCurrentLocation,
  requestGeolocationPermission,
  afterSubmit,
}) => {
  const [shouldShowSuccessModal, setShouldShowSuccessModal] = useState(false);
  const [scannerState, setScannerState] = useState(SCANNER_STATE_CLOSED);
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  const dispatch = useDispatch();

  const _getInitialValues = () => {
    return {
      type: '',
      serialNumber: '',
    };
  };

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);

    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();
      setIsLocationLoading(false);

      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });

      setIsLocationLoading(false);
      throw error;
    }
  };

  const _onSubmit = async (values, formikBag) => {
    try {
      const location = await _requestGeolocationPermission();
      const { latitude, longitude } = location;
      const { type, serialNumber, status } = values;
      return dispatch(postWithState, {
        action: SR_API_ACTION_INVENTORY_ITEM_SCAN,
        payload: {
          type: itemType || type,
          serialNumber,
          latitude,
          longitude,
          status,
        },
        onSuccess: () => {
          if (formType === FORM_TYPE_REGISTER) {
            setShouldShowSuccessModal(true);

            setTimeout(() => {
              setShouldShowSuccessModal(false);
            }, 2000);

            setScannerState(SCANNER_STATE_CLOSED);
            formikBag.resetForm();
          }

          if (afterSubmit) {
            afterSubmit();
          }
        },
        onError: (error) => {
          global.alert(error.message);
          formikBag.resetForm();
        },
      });
    } catch (error) {
      global.alert(error.message);
    }
  };

  const initialFormFields = getFormFields({ formType, scannerState });
  const initialValues = _getInitialValues();

  const setSerialNumber = ({ formikBag, serialNumber }) => {
    formikBag.setFieldValue('serialNumber', serialNumber);
    setScannerState(SCANNER_STATE_MANUAL);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={createFormValidationFn(initialFormFields)}
        onSubmit={_onSubmit}
        render={(formikBag) => {
          const formFields = getFormFields({ formType, scannerState });
          return (
            <Form>
              {formFields.map((formField, index) => (
                <SrFormikField
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  margin={index === 0 ? '0' : null}
                  key={formField.id}
                />
              ))}
              {scannerState !== SCANNER_STATE_MANUAL && (
                <SrBox paddingTop="28px">
                  <SrButton
                    onClick={() => setScannerState(SCANNER_STATE_OPEN)}
                    theme={SR_BUTTON_THEME_ADOBE_BLUE_BORDER}
                  >
                    Scan Barcode
                  </SrButton>
                  <SrBox
                    margin="12px 0px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <SrText
                      color="grey400"
                      font="greycliff"
                      fontSize="16px"
                      lineHeight="16px"
                      weight="600"
                      align="center"
                    >
                      or
                    </SrText>
                  </SrBox>
                  <SrBox
                    margin="12px 0px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <SrText
                      color="blue300"
                      font="greycliff"
                      fontSize="16px"
                      lineHeight="16px"
                      weight="800"
                      align="center"
                      cursor="pointer"
                      onClick={() => setScannerState(SCANNER_STATE_MANUAL)}
                    >
                      Manually Input Code
                    </SrText>
                  </SrBox>
                </SrBox>
              )}
              <SrBox paddingTop="40px">
                <SrButton
                  type="submit"
                  disabled={
                    formikBag.isSubmitting ||
                    !formikBag.isValid ||
                    (formikBag.values.serialNumber || '').length === 0
                  }
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                >
                  {isLocationLoading
                    ? 'Getting Location'
                    : formikBag.isSubmitting
                    ? 'Submitting'
                    : 'Submit'}
                </SrButton>
              </SrBox>
              {scannerState === SCANNER_STATE_OPEN && (
                <SrBarcodeScanner
                  onText={(text) => setSerialNumber({ formikBag, serialNumber: text })}
                  onClose={() => setScannerState(SCANNER_STATE_CLOSED)}
                />
              )}
              {shouldShowSuccessModal && (
                <SrSimpleMessageModal
                  iconUrl="/images/checkmark-circle-large-green.svg"
                  headline="Success!"
                  maxWidth={400}
                  minWidth={400}
                  theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                />
              )}
            </Form>
          );
        }}
      />
    </>
  );
};

export default SrInventoryItemScanForm;
